import { z } from 'zod';
import { EquipmentCategory, MarketplaceCurrency } from './marketplace.enums';
import { PaginationParamsSchema } from '../common';
import { VendorRecentReviewSchema } from '../booking/review.schemas';
import { VendorTeamSize } from '../user/user.enums';

export const ListVendorsQuerySchema = z.object({
  category: z.string().optional(),
  /**
   * Optional service-category code filter (vendor “role” alias).
   * Same matching as `category`. Vendors may have multiple categories.
   * Known codes include: sound, lighting, video, staging, av, logistics.
   */
  role: z.string().trim().min(1).max(100).optional(),
  minRate: z.coerce.number().int().min(0).optional(),
  maxRate: z.coerce.number().int().min(0).optional(),
  availabilityDate: z.string().optional(),
  /** Case-insensitive match on name, company, role/skill, or equipment */
  search: z.string().trim().min(2).max(100).optional(),
  ...PaginationParamsSchema.shape,
});

export const PublicVendorListEquipmentItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  category: z.enum(EquipmentCategory),
  notes: z.string().nullable(),
});

export const PublicVendorListCategorySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  label: z.string(),
});

export const ServiceCategoryListResponseSchema = z.object({
  items: z.array(PublicVendorListCategorySchema),
});

export const PublicVendorListPortfolioItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  mediaUrl: z.url().nullable(),
  externalUrl: z.url().nullable(),
  createdAt: z.iso.datetime(),
});

export const PublicVendorListRateCardItemSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  amount: z.string(),
  currency: z.enum(MarketplaceCurrency),
  notes: z.string().nullable(),
});

/**
 * Enum values match the Prisma `MessageKind` column. Live wire type
 * for hiring-thread messages.
 */
export const HiringThreadMessageKindSchema = z.enum(['USER', 'SYSTEM']);

/** Matches the Prisma `MessageDeliveryStatus` column. */
export const HiringThreadMessageDeliveryStatusSchema = z.enum(['SENT', 'DELIVERED', 'READ']);

/**
 * Structured payload attached to a `SYSTEM` message. Discriminated on
 * `kind` — the set is intentionally narrow. Extend the union as new
 * automated messages get added (booking accepted, cancelled, etc.).
 */
export const HiringThreadSystemMessagePayloadSchema = z.object({
  kind: z.literal('EVENT_RESCHEDULED'),
  eventId: z.string(),
  fromStart: z.iso.datetime(),
  toStart: z.iso.datetime(),
});

/**
 * Message attachment shape rendered by the hiring-thread inbox +
 * socket fan-out. `fileName` is normalised server-side (falls back to
 * the storage key basename if the client didn't send one).
 */
export const HiringThreadMessageAttachmentSchema = z.object({
  id: z.string(),
  url: z.url(),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive(),
  fileName: z.string(),
  createdAt: z.iso.datetime(),
});

/**
 * Full hiring-thread message view — the shape returned by the socket
 * post ACK and by the fan-out event. Kept in one schema so a shared FE
 * renderer can accept either source.
 */
export const HiringThreadMessageSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  /** `null` for SYSTEM messages (Livdot-authored). */
  senderUserId: z.string().nullable(),
  messageKind: HiringThreadMessageKindSchema,
  systemPayload: HiringThreadSystemMessagePayloadSchema.nullable(),
  body: z.string(),
  sentAt: z.iso.datetime(),
  deliveryStatus: HiringThreadMessageDeliveryStatusSchema,
  readAt: z.iso.datetime().nullable(),
  attachments: z.array(HiringThreadMessageAttachmentSchema),
});

/** ACK for `hiring_thread.message.post`. */
export const HiringThreadPostAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    message: HiringThreadMessageSchema,
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum([
      'unauthorized',
      'invalid_payload',
      'forbidden',
      'not_found',
      'messaging_closed',
      'rate_limited',
      'internal',
    ]),
    message: z.string(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
  }),
]);

/** ACK for `hiring_thread.message.read`. */
export const HiringThreadReadAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    markedCount: z.number().int().nonnegative(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['unauthorized', 'invalid_payload', 'forbidden', 'not_found', 'internal']),
    message: z.string(),
  }),
]);

/** ACK for `hiring_thread.join` / `hiring_thread.leave`. */
export const HiringThreadJoinAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    threadId: z.string(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['unauthorized', 'invalid_payload', 'forbidden', 'not_found']),
    message: z.string(),
  }),
]);

/** One element of `ListVendorsResponse.vendors`. */
export const PublicVendorListItemSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  displayName: z.string(),
  avatarUrl: z.url().nullable(),
  vendorIsVerified: z.boolean(),
  companyName: z.string().nullable(),
  bio: z.string().nullable(),
  yearsExperience: z.number().int().nullable(),
  teamSize: z.enum(VendorTeamSize).nullable(),
  maxDistanceKm: z.number().int().nullable(),
  isAvailable: z.boolean(),
  averageRating: z.string(),
  totalReviews: z.number().int(),
  completionRate: z.string(),
  equipmentManifest: z.array(PublicVendorListEquipmentItemSchema),
  serviceCategories: z.array(PublicVendorListCategorySchema),
  portfolio: z.array(PublicVendorListPortfolioItemSchema),
  equipmentItems: z.array(PublicVendorListCategorySchema),
  rateCard: z.array(PublicVendorListRateCardItemSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  recentReviews: z.array(VendorRecentReviewSchema).optional(),
});
